<template>
  <div>
    <pa-admin-toolbar
      :per-page="perPage"
      :search.sync="searchInput"
      :from.sync="fromInput"
      :to.sync="toInput"
      :busy="loading"
      @per-page="setPerPage"
      @search="applySearch"
    >
      <button type="button" class="paa-btn paa-btn--secondary" :disabled="loading || !total" @click="exportRows">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M12 3v12m0 0 4-4m-4 4-4-4M4 21h16" /></svg>
        Export CSV
      </button>
    </pa-admin-toolbar>

    <div class="paa-filters">
      <pa-admin-filter label="SEO Manager" :value="filters.campaignManager" :options="managerOptions" @input="setFilter('campaignManager', $event)" />
      <pa-admin-filter label="Stage" :value="filters.stage" :options="stageOptions" @input="setFilter('stage', $event)" />
      <pa-admin-filter label="Package" :value="filters.package" :options="packageOptions" @input="setFilter('package', $event)" />
    </div>

    <div v-if="loadError" class="paa-error">{{ loadError }}</div>

    <div class="paa-stat-row">
      <span class="paa-muted">{{ statLine }}</span>
      <span class="paa-spacer"></span>
      <b>Total paid: {{ money(totalPaid) || '$0.00' }}</b>
    </div>

    <div class="paa-card paa-tblwrap">
      <table class="paa-tbl">
        <thead>
          <tr>
            <th :class="thClass('userId')" @click="sortBy('userId')">{{ sortMark('userId') }}ID</th>
            <th :class="thClass('userEmail')" @click="sortBy('userEmail')">{{ sortMark('userEmail') }}Email</th>
            <th :class="thClass('joined')" @click="sortBy('joined')">{{ sortMark('joined') }}Joined</th>
            <th>Status</th>
            <th>SEO Manager</th>
            <th>Package</th>
            <th :class="thClass('paidMonths', 'paa-r')" @click="sortBy('paidMonths')">{{ sortMark('paidMonths') }}Month</th>
            <th>Setup fee</th>
            <th :class="thClass('firstTransactionAt')" @click="sortBy('firstTransactionAt')">{{ sortMark('firstTransactionAt') }}First transaction</th>
            <th :class="thClass('lastTransactionAt')" @click="sortBy('lastTransactionAt')">{{ sortMark('lastTransactionAt') }}Last transaction</th>
            <th class="paa-r">Last amount</th>
            <th :class="thClass('lifetimePaid', 'paa-r')" @click="sortBy('lifetimePaid')">{{ sortMark('lifetimePaid') }}Lifetime paid</th>
            <th>Cancelled on</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in records" :key="row.key">
            <td>{{ row.userId }}</td>
            <td>
              <a :href="userAccountsHref(row.userEmail)" target="_blank" rel="noopener">{{ row.userEmail }}</a>
              <span class="paa-sub">{{ row.siteName }}</span>
            </td>
            <td>{{ dateOnly(row.joined) }}</td>
            <td><pa-admin-badge :badge="row.status" :title="statusTitle(row)" /></td>
            <td>{{ managerLabel(row) }}</td>
            <td>
              <template v-if="row.package">
                {{ row.package.name }}
                <span v-if="row.package.custom" class="paa-muted">({{ row.package.tl }} TL x {{ row.package.per }})</span>
              </template>
              <span v-else class="paa-muted">No package</span>
            </td>
            <td class="paa-r">{{ row.package && row.paidMonths ? `Month ${row.paidMonths}` : '' }}</td>
            <td>
              <template v-if="row.setupFee && row.setupFee.paid">
                Paid
                <span
                  v-if="row.setupFee.coupon"
                  class="paa-badge paa-badge--info"
                  :title="`Coupon applied: ${row.setupFee.coupon}`"
                >{{ money(row.setupFee.amount) }}</span>
                <template v-else>{{ money(row.setupFee.amount) }}</template>
              </template>
              <span v-else class="paa-muted">Unpaid</span>
            </td>
            <td>{{ row.firstTransactionAt || '' }}</td>
            <td>{{ row.lastTransactionAt || '' }}</td>
            <td class="paa-r">{{ row.lastAmount ? money(row.lastAmount) : '' }}</td>
            <td class="paa-r paa-num">{{ money(row.lifetimePaid) || '$0.00' }}</td>
            <td>{{ dateOnly(row.cancelledOn) }}</td>
          </tr>
          <tr v-if="!records.length">
            <td class="paa-empty" colspan="13">{{ loading ? 'Loading…' : 'No project matches this search.' }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <pa-admin-pager :page="page" :page-count="pageCount" @change="setPage" />
  </div>
</template>

<script>
import PaAdminBadge from './PaAdminBadge.vue'
import PaAdminFilter from './PaAdminFilter.vue'
import PaAdminPager from './PaAdminPager.vue'
import PaAdminToolbar from './PaAdminToolbar.vue'
import { PA_ADMIN_PACKAGE_OPTIONS_WITH_NONE, PA_ADMIN_STAGE_OPTIONS } from './paAdminConstants'
import paAdminListMixin from './paAdminListMixin'

/**
 * Customer Overview: one row per POP Authority project (a customer with several projects has
 * several rows, the project name under the email). The status is the one the client sees on the
 * project's Overview; the date range applies to the day the project was created.
 */
export default {
  name: 'AdminPopAuthorityCustomerOverview',
  components: { PaAdminBadge, PaAdminFilter, PaAdminPager, PaAdminToolbar },
  mixins: [paAdminListMixin],
  paAdmin: { action: 'getPopAuthorityCustomersForAdmin', sort: 'joined', tab: 'customers' },
  data () {
    return {
      filters: { campaignManager: 'all', stage: 'all', package: 'all' },
      stageOptions: PA_ADMIN_STAGE_OPTIONS,
      packageOptions: PA_ADMIN_PACKAGE_OPTIONS_WITH_NONE,
    }
  },
  computed: {
    statLine () {
      const projects = this.stats.projectsCount || 0
      const customers = this.stats.customersCount || 0
      return `${projects} ${projects === 1 ? 'project' : 'projects'} of ${customers} ` +
        `${customers === 1 ? 'customer' : 'customers'} joined in range`
    },
  },
  methods: {
    managerLabel (row) {
      const manager = row.manager || {}
      return manager.isDefault ? `${manager.name} (Default)` : (manager.name || '')
    },
    statusTitle (row) {
      const status = row.status || {}
      return status.engineKey && status.engineKey !== status.key ? `Project status: ${status.engineKey}` : ''
    },
    exportRows () {
      const header = [
        'ID', 'Email', 'Project', 'Joined', 'Status', 'SEO Manager', 'Package', 'Month', 'Setup fee',
        'Setup fee coupon', 'First transaction', 'Last transaction', 'Last amount ($)', 'Lifetime paid ($)',
        'Cancelled on',
      ]
      return this.exportCsv(header, (row) => [
        row.userId,
        row.userEmail,
        row.siteName,
        this.dateOnly(row.joined),
        (row.status && row.status.label) || '',
        this.managerLabel(row),
        row.package ? row.package.name + (row.package.custom ? ` (${row.package.tl} TL x ${row.package.per})` : '') : 'No package',
        row.package && row.paidMonths ? row.paidMonths : '',
        row.setupFee && row.setupFee.paid ? row.setupFee.amount : 'Unpaid',
        (row.setupFee && row.setupFee.coupon) || '',
        row.firstTransactionAt || '',
        row.lastTransactionAt || '',
        row.lastAmount || '',
        row.lifetimePaid || 0,
        this.dateOnly(row.cancelledOn),
      ])
    },
  },
}
</script>
